import {
  accountIdFormat,
  customReservedInventoryBucketTypeIdFormat,
  inventoryItemIdFormat,
  locationIdFormat,
  productIdFormat,
  storageLocationIdFormat,
} from "@redotech/redo-ids/ids";
import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod";

export enum StorageLocationType {
  BIN = "bin",
  TOTE = "tote",
  STORAGE = "storage",
  RECEIVING = "receiving",
  EXTERNAL = "external",
  LOST = "lost",
  TRANSIT = "transit",
  UNMAPPED = "unmapped",
  DEFAULT = "default",
  WASTE = "waste",
  FLUSH = "flush",
  STATION = "station",
  SORTING_TOTE = "sorting_tote",
}

// these show up in the bin table and are not transient, (totes or carts)
export const PersistentLocationTypes: StorageLocationType[] = [
  StorageLocationType.BIN,
  StorageLocationType.STORAGE,
  StorageLocationType.RECEIVING,
  StorageLocationType.STATION,
  StorageLocationType.SORTING_TOTE,
];

export const CycleCountableLocationTypes: StorageLocationType[] = [
  StorageLocationType.BIN,
  StorageLocationType.TOTE,
  StorageLocationType.STORAGE,
  StorageLocationType.DEFAULT,
  StorageLocationType.RECEIVING,
];

export const PersistentStorageLocationTypeSchema = createEnum(
  [
    StorageLocationType.BIN,
    StorageLocationType.STORAGE,
    StorageLocationType.RECEIVING,
    StorageLocationType.STATION,
    StorageLocationType.SORTING_TOTE,
  ],
  { enumName: "PersistentStorageLocationType" },
);
export type PersistentStorageLocationType = z.infer<
  typeof PersistentStorageLocationTypeSchema
>;

export const BinPriority = ["primary", "secondary"] as const;
export type BinPriority = (typeof BinPriority)[number];
export const BinPrioritySchema = createEnum(BinPriority, {
  enumName: "BinPriority",
});

export enum StorageLocationSource {
  MANUAL = "manual",
  WAREHOUSE_MAPPER = "warehouse_mapper",
  SYSTEM = "system",
}

export const StorageLocationTypeSchema = createEnum(StorageLocationType);
export const StorageLocationSourceSchema = createEnum(StorageLocationSource);

export const ReservedBucketCategorySchema = z
  .object({
    type: z.literal("custom"),
    customTypeId: customReservedInventoryBucketTypeIdFormat.schema,
  })
  .nullable();
export type ReservedBucketCategory = z.infer<
  typeof ReservedBucketCategorySchema
>;

export const StorageLocationSchema = z.object({
  id: storageLocationIdFormat.schema,
  accountId: accountIdFormat.schema,
  locationId: locationIdFormat.schema,
  name: z.string(),
  type: StorageLocationTypeSchema,
  source: StorageLocationSourceSchema,
  isSellable: z.boolean(),
  reservedBucketCategory: ReservedBucketCategorySchema,
  deletedAt: z.date().nullable(),
  createdAt: z.date(),
  updatedAt: z.date(),
});

export type StorageLocation = z.infer<typeof StorageLocationSchema>;
export interface IStorageLocation extends z.ZodType<StorageLocation> {}

export const StorageLocationInventoryItemSchema = z.object({
  productInfo: z
    .object({
      id: productIdFormat.schema,
      title: z.string(),
      imageUrl: z.string().nullish(),
      sku: z.string().nullish(),
      barcode: z.string().nullish(),
      barcodes: z.array(z.string()).nullish(),
    })
    .nullish(),
  inventoryItemId: inventoryItemIdFormat.schema.nullish(),
  inventoryItemKind: z.string().nullish(),
  quantity: z.number(),
  lastCountedAt: z.coerce.date().nullish(),
  lotNumber: z.string().nullish(),
  expirationDate: z.coerce.date().nullish(),
});

export const StorageLocationTableRowSchema = StorageLocationSchema.extend({
  inventoryItems: z.array(StorageLocationInventoryItemSchema),
  locationName: z.string(),
});

export type StorageLocationTableRow = z.infer<
  typeof StorageLocationTableRowSchema
>;
